import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSearchParams } from 'react-router-dom';

const TEXT_COLOR = '#eeeeee';

export default function Upgrade() {
  const { apiFetch } = useAuth();
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from') || '';
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    apiFetch('/api/admin/upgrade-status')
      .then(data => setStatus(data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [apiFetch]);

  const parentLink = status?.joinCode
    ? `${window.location.origin}/parents/join/${status.joinCode}`
    : `${window.location.origin}/parent/order`;

  const handleCopy = () => {
    navigator.clipboard.writeText(parentLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) return <div className="admin-page"><div className="loading"><div className="spinner" /></div></div>;

  if (status?.isPaid && !status?.gracePeriod) {
    return (
      <div className="admin-page">
        <h1 className="page-title" style={{ color: TEXT_COLOR }}>Upgrade</h1>
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <div style={{ fontSize: '2rem', marginBottom: 12 }}>{'\u2705'}</div>
          <h2 style={{ marginBottom: 8, color: TEXT_COLOR }}>You have full access</h2>
          <p style={{ color: TEXT_COLOR, fontSize: '0.9rem' }}>
            {status.hasClubSub
              ? 'Your club has an active subscription.'
              : `${status.paidPlayers} of ${status.totalPlayers} players have paying parents (${status.percentage}%). That meets the 50% threshold.`
            }
          </p>
          {status.promoTrial && (
            <p style={{ color: 'var(--orange)', fontSize: '0.85rem', marginTop: 8 }}>
              Promo trial active until {new Date(status.promoTrial.end).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <h1 className="page-title" style={{ color: TEXT_COLOR }}>Unlock All Features</h1>

      {status?.gracePeriod && (
        <div style={{ background: 'rgba(243,156,18,0.12)', border: '1px solid rgba(243,156,18,0.3)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: '0.85rem' }}>
          <strong style={{ color: '#f39c12' }}>Grace period active.</strong>
          <span style={{ color: TEXT_COLOR, marginLeft: 8 }}>
            Your team dropped below 50% paid players. Paid features stay unlocked until {new Date(status.gracePeriod.end).toLocaleDateString()}.
          </span>
        </div>
      )}

      <p style={{ color: TEXT_COLOR, fontSize: '0.9rem', marginBottom: 24 }}>
        Free coaches get the core tools: roster, drills, and leaderboard. Unlock everything else — reports, push notifications, email blasts, custom challenges, and more — through one of two paths.
      </p>

      {/* Option A: Parent-Pays */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: '1.1rem', marginBottom: 8, color: TEXT_COLOR }}>Option A: Parents Pay</h2>
        <p style={{ color: TEXT_COLOR, fontSize: '0.85rem', marginBottom: 12 }}>
          When 50% or more of your players have a parent with an active Train Daily subscription, your coach features unlock automatically. No cost to you or your club.
        </p>

        {status && (
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 8, padding: 16, marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: TEXT_COLOR }}>Current Status</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 700, color: status.percentage >= 50 ? 'var(--success)' : 'var(--orange)' }}>
                {status.percentage}%
              </span>
            </div>
            <div style={{ background: 'var(--dark-bg)', borderRadius: 6, height: 8, overflow: 'hidden' }}>
              <div style={{ width: `${Math.min(status.percentage, 100)}%`, height: '100%', background: status.percentage >= 50 ? 'var(--success)' : 'var(--orange)', borderRadius: 6, transition: 'width 0.3s' }} />
            </div>
            <div style={{ fontSize: '0.8rem', color: TEXT_COLOR, marginTop: 6 }}>
              {status.paidPlayers} of {status.totalPlayers} players have paying parents
              {status.totalPlayers > 0 && status.percentage < 50 && (
                <span> — need {Math.ceil(status.totalPlayers / 2) - status.paidPlayers} more</span>
              )}
            </div>
          </div>
        )}

        <p style={{ fontSize: '0.85rem', color: TEXT_COLOR }}>
          Parents pay $5.99/month (or $3.99/month billed yearly) per player. Their kids get daily video drills, quizzes, points, badges, and a leaderboard — all from their phone. You can share this link with parents:
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <div style={{ background: 'var(--dark-bg)', borderRadius: 8, padding: '10px 14px', fontSize: '0.85rem', fontFamily: 'monospace', wordBreak: 'break-all', flex: 1, color: TEXT_COLOR }}>
            {parentLink}
          </div>
          <button
            onClick={handleCopy}
            style={{ background: '#fff', color: '#000', border: 'none', borderRadius: 8, padding: '10px 14px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
        </div>
      </div>

      {/* Option B: Club Pays */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: '1.1rem', marginBottom: 8, color: TEXT_COLOR }}>Option B: Club / Coach Pays</h2>
        <p style={{ color: TEXT_COLOR, fontSize: '0.85rem', marginBottom: 12 }}>
          Your club or coaching staff pays a single subscription that covers the entire team. All coach features unlock immediately, and every player gets full access.
        </p>

        {status?.hasClubSub ? (
          <div style={{ color: 'var(--success)', fontWeight: 600, fontSize: '0.9rem' }}>
            {'\u2705'} Your club already has an active subscription.
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <a href="/coaches/order-monthly" className="btn btn-orange" style={{ textDecoration: 'none', minWidth: 220, textAlign: 'center' }}>
              Subscribe Monthly
            </a>
            <a href="/coaches/order-yearly" className="btn btn-blue" style={{ textDecoration: 'none', minWidth: 220, textAlign: 'center' }}>
              Subscribe Yearly (save 33%)
            </a>
          </div>
        )}
      </div>

      {/* Benefits List */}
      <div className="card">
        <h3 style={{ fontSize: '0.95rem', marginBottom: 12, color: TEXT_COLOR }}>What you get with paid access</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[
            'Detailed player and drill reports',
            'Push notifications to players',
            'Email blasts to parents',
            'Custom challenges and seasons',
            'Unlimited questions on drills',
            'Unlimited coaches per team',
            'Premium drills from the library',
            'No ads for your players',
          ].map((benefit, i) => (
            <div key={i} style={{ fontSize: '0.85rem', color: TEXT_COLOR }}>
              <span style={{ color: 'var(--success)', marginRight: 8 }}>{'\u2713'}</span>
              {benefit}
            </div>
          ))}
        </div>
      </div>

      {from && <input type="hidden" data-from={from} />}
    </div>
  );
}
